import { useState } from 'react';
import { useLang } from '../lib/lang';
import { IMAGES } from '../content/constants';
import { Page, HeroShell, Reveal, Overline } from '../components/motion';
import { CTASection, PageSection, SectionHead } from '../components/blocks';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const LOGO_OVERRIDES = {
  'PNUD': 'undp',
  'Cruz Vermelha Libanesa': 'lebanese-red-cross',
};

const ClientTile = ({ name }) => {
  const file = LOGO_OVERRIDES[name] || slug(name);
  const [src, setSrc] = useState(`/logos/${file}.png`);
  const [failed, setFailed] = useState(false);
  return (
    <div data-testid={`client-tile-${slug(name)}`} className="group">
      <div className="h-24 md:h-28 bg-[#ECECE8] flex items-center justify-center px-6 group-hover:bg-white transition-colors duration-300">
        {failed ? (
          <span className="font-display font-black text-zinc-800 text-center text-xl md:text-2xl tracking-tight leading-none uppercase">
            {name.split(' ').map((w) => w[0]).join('').slice(0, 3)}
          </span>
        ) : (
          <img
            src={src}
            alt={`${name} logo`}
            onError={() => (src.endsWith('.png') ? setSrc(`/logos/${file}.svg`) : setFailed(true))}
            className="max-h-12 md:max-h-14 w-auto grayscale contrast-125 opacity-80 group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-300"
          />
        )}
      </div>
      <p className="mt-2.5 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 leading-tight group-hover:text-volt transition-colors duration-300">
        {name}
      </p>
    </div>
  );
};

export default function References() {
  const { t } = useLang();
  const c = t.refs;
  return (
    <Page meta={{ ...t.meta.refs, lang: t.lang }}>
      <HeroShell testId="refs-hero" overline={c.hero.overline} lines={c.hero.lines} sub={c.hero.sub} image={IMAGES.serverOps} />

      <section data-testid="refs-track" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHead overline={c.track.overline} />
          <div className="mt-10 grid md:grid-cols-2 gap-10">
            <Reveal><p className="text-base text-zinc-400 leading-relaxed border-l-2 border-volt pl-6">{c.track.angola}</p></Reveal>
            <Reveal delay={0.1}><p className="text-base text-zinc-400 leading-relaxed border-l-2 border-white/20 pl-6">{c.track.international}</p></Reveal>
          </div>
        </div>
      </section>

      <PageSection overline={c.wall.overline} testId="refs-wall" className="bg-surface/40">
        <Reveal><p className="-mt-6 mb-14 text-sm text-zinc-500">{c.wall.sub}</p></Reveal>
        <div className="space-y-14">
          {c.wall.groups.map((g, gi) => (
            <div key={gi} data-testid={`client-group-${gi}`}>
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-volt mb-5">{g.label}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-6">
                {g.clients.map((name) => (
                  <ClientTile key={name} name={name} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </PageSection>

      <section data-testid="refs-countries" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHead overline={c.countries.overline} />
          </div>
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4 self-center">
            {c.countries.list.map((country, i) => (
              <Reveal key={country} delay={i * 0.03}>
                <p className="font-mono text-xs text-zinc-400 flex items-baseline gap-3">
                  <span className="text-volt text-[10px]">{String(i + 1).padStart(2, '0')}</span>
                  {country}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="refs-sectors" className="border-t border-white/10 bg-surface/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Overline>{c.sectors.overline}</Overline>
          <div className="mt-8 flex flex-wrap gap-3">
            {c.sectors.list.map((s, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <span className="inline-block border border-white/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 hover:border-volt hover:text-volt transition-colors duration-300">
                  {s}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection title={c.cta} btn={c.cta} />
    </Page>
  );
}
